import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import MainLayout from '../../components/layout/MainLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription
} from '../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../../components/ui/dropdown-menu';
import { ScrollArea } from '../../components/ui/scroll-area';
import {
  Plus,
  Search,
  Loader2,
  CheckCircle2,
  Clock,
  Code2,
  TestTube2,
  ShieldCheck,
  Lightbulb,
  Rocket,
  MoreVertical,
  Pencil,
  Trash2,
  Mic,
  MicOff,
  Upload,
  Link,
  Image as ImageIcon,
  X,
  Users,
  Building2,
  User,
  Globe,
  BarChart3,
  Download,
  RefreshCw,
  Filter,
  MessageSquare,
  Send
} from 'lucide-react';
import { toast } from 'sonner';
import api from '../../lib/axios';
import { cn } from '../../lib/utils';

// Status configuration
const STATUS_CONFIG = {
  validated: { 
    label: 'Validée', 
    color: 'bg-green-100 text-green-700 border-green-200',
    icon: ShieldCheck
  },
  developed: { 
    label: 'Développée', 
    color: 'bg-blue-100 text-blue-700 border-blue-200',
    icon: Code2
  },
  tested: { 
    label: 'Testée', 
    color: 'bg-purple-100 text-purple-700 border-purple-200',
    icon: TestTube2
  },
  in_development: { 
    label: 'En développement', 
    color: 'bg-amber-100 text-amber-700 border-amber-200',
    icon: Clock
  },
  upcoming: { 
    label: 'À venir', 
    color: 'bg-slate-100 text-slate-700 border-slate-200',
    icon: Rocket
  },
  idea: { 
    label: 'Idée', 
    color: 'bg-pink-100 text-pink-700 border-pink-200',
    icon: Lightbulb
  }
};

// Space configuration
const SPACE_CONFIG = {
  client: { label: 'Client', icon: User, color: 'text-blue-600 bg-blue-50' },
  team_member: { label: 'Équipe', icon: Users, color: 'text-emerald-600 bg-emerald-50' },
  agency: { label: 'Agence', icon: Building2, color: 'text-purple-600 bg-purple-50' },
  global: { label: 'Global', icon: Globe, color: 'text-slate-600 bg-slate-50' }
};

// Category configuration
const CATEGORIES = [
  { value: 'dashboard', label: 'Tableau de bord' },
  { value: 'projects', label: 'Projets' },
  { value: 'calendar', label: 'Calendrier' },
  { value: 'tasks', label: 'Tâches' },
  { value: 'time_tracking', label: 'Suivi du temps' },
  { value: 'invoices', label: 'Facturation' },
  { value: 'contracts', label: 'Contrats' },
  { value: 'tickets', label: 'Tickets' },
  { value: 'chat', label: 'Chat' },
  { value: 'settings', label: 'Paramètres' },
  { value: 'reports', label: 'Rapports' },
  { value: 'notifications', label: 'Notifications' },
  { value: 'auth', label: 'Authentification' },
  { value: 'other', label: 'Autre' }
];

const PRIORITY_CONFIG = {
  low: { label: 'Basse', color: 'bg-slate-100 text-slate-600' },
  medium: { label: 'Moyenne', color: 'bg-blue-100 text-blue-600' },
  high: { label: 'Haute', color: 'bg-orange-100 text-orange-600' },
  critical: { label: 'Critique', color: 'bg-red-100 text-red-600' }
};

const FeatureTrackingPage = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [seeding, setSeeding] = useState(false);
  const [features, setFeatures] = useState([]);
  const [stats, setStats] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [spaceFilter, setSpaceFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [categoryFilter, setCategoryFilter] = useState('all');
  
  // Dialog states
  const [showDialog, setShowDialog] = useState(false);
  const [editingFeature, setEditingFeature] = useState(null);
  const [savingFeature, setSavingFeature] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  
  // Comments states
  const [showCommentsDialog, setShowCommentsDialog] = useState(false);
  const [selectedFeatureForComments, setSelectedFeatureForComments] = useState(null);
  const [comments, setComments] = useState([]);
  const [loadingComments, setLoadingComments] = useState(false);
  const [newComment, setNewComment] = useState('');
  const [sendingComment, setSendingComment] = useState(false);
  
  // Voice recording
  const [isRecording, setIsRecording] = useState(false);
  const [isTranscribing, setIsTranscribing] = useState(false);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  
  // Form state
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    space: 'client',
    category: 'dashboard',
    status: 'idea',
    priority: 'medium',
    brief: '',
    target_version: ''
  });

  const fetchFeatures = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (spaceFilter !== 'all') params.append('space', spaceFilter);
      if (statusFilter !== 'all') params.append('status', statusFilter);
      if (categoryFilter !== 'all') params.append('category', categoryFilter);
      
      const [featuresRes, statsRes] = await Promise.all([
        api.get(`/features/?${params.toString()}`),
        api.get('/features/stats')
      ]);
      
      setFeatures(featuresRes.data || []);
      setStats(statsRes.data);
    } catch (error) {
      console.error('Error fetching features:', error);
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  }, [spaceFilter, statusFilter, categoryFilter]);

  useEffect(() => {
    fetchFeatures();
  }, [fetchFeatures]);

  // ======================= COMMENTS FUNCTIONS =======================
  const fetchComments = async (featureId) => {
    setLoadingComments(true);
    try {
      const res = await api.get(`/features/${featureId}/comments`);
      setComments(res.data || []);
    } catch (error) {
      console.error('Error fetching comments:', error);
      toast.error('Erreur lors du chargement des commentaires');
    } finally {
      setLoadingComments(false);
    }
  };

  const openCommentsDialog = (feature) => {
    setSelectedFeatureForComments(feature);
    setShowCommentsDialog(true);
    fetchComments(feature.id);
  };

  const handleAddComment = async () => {
    if (!newComment.trim() || !selectedFeatureForComments) return;
    
    setSendingComment(true);
    try {
      await api.post(`/features/${selectedFeatureForComments.id}/comments`, {
        content: newComment
      });
      setNewComment('');
      fetchComments(selectedFeatureForComments.id);
      toast.success('Commentaire ajouté');
    } catch (error) {
      toast.error('Erreur lors de l\'ajout du commentaire');
    } finally {
      setSendingComment(false);
    }
  };

  const handleDeleteComment = async (commentId) => {
    if (!selectedFeatureForComments) return;
    
    try {
      await api.delete(`/features/${selectedFeatureForComments.id}/comments/${commentId}`);
      fetchComments(selectedFeatureForComments.id);
      toast.success('Commentaire supprimé');
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const formatCommentDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);
    
    if (diffMins < 1) return 'À l\'instant';
    if (diffMins < 60) return `Il y a ${diffMins} min`;
    if (diffHours < 24) return `Il y a ${diffHours}h`;
    if (diffDays < 7) return `Il y a ${diffDays}j`;
    return date.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' });
  };

  // ======================= SEED & FORM FUNCTIONS =======================
  const handleSeedFeatures = async () => {
    setSeeding(true);
    try {
      const response = await api.post('/features/seed-existing');
      toast.success(response.data.message);
      fetchFeatures();
    } catch (error) {
      toast.error('Erreur lors de l\'initialisation');
    } finally {
      setSeeding(false);
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      space: 'client',
      category: 'dashboard',
      status: 'idea',
      priority: 'medium',
      brief: '',
      target_version: ''
    });
    setEditingFeature(null);
  };

  const openCreateDialog = () => {
    resetForm();
    setShowDialog(true);
  };

  const openEditDialog = (feature) => {
    setEditingFeature(feature);
    setFormData({
      name: feature.name || '',
      description: feature.description || '',
      space: feature.space || 'client',
      category: feature.category || 'dashboard',
      status: feature.status || 'idea',
      priority: feature.priority || 'medium',
      brief: feature.brief || '',
      target_version: feature.target_version || ''
    });
    setShowDialog(true);
  };

  const handleSave = async () => {
    if (!formData.name.trim()) {
      toast.error('Veuillez donner un nom à la fonctionnalité');
      return;
    }

    setSavingFeature(true);
    try {
      if (editingFeature) {
        await api.put(`/features/${editingFeature.id}`, formData);
        toast.success('Fonctionnalité mise à jour');
      } else {
        await api.post('/features/', formData);
        toast.success('Fonctionnalité créée');
      }
      setShowDialog(false);
      resetForm();
      fetchFeatures();
    } catch (error) {
      toast.error('Erreur lors de la sauvegarde');
    } finally {
      setSavingFeature(false);
    }
  };

  const handleDelete = async (featureId) => {
    if (!window.confirm('Supprimer cette fonctionnalité ?')) return;
    
    setDeletingId(featureId);
    try {
      await api.delete(`/features/${featureId}`);
      toast.success('Fonctionnalité supprimée');
      fetchFeatures();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    } finally {
      setDeletingId(null);
    }
  };

  const handleStatusChange = async (featureId, newStatus) => {
    try {
      await api.put(`/features/${featureId}`, { status: newStatus });
      toast.success('Statut mis à jour');
      fetchFeatures();
    } catch (error) {
      toast.error('Erreur lors de la mise à jour');
    }
  };

  // Voice recording functions
  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mediaRecorder = new MediaRecorder(stream, {
        mimeType: MediaRecorder.isTypeSupported('audio/webm') ? 'audio/webm' : 'audio/mp4'
      });
      
      mediaRecorderRef.current = mediaRecorder;
      audioChunksRef.current = [];
      
      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };
      
      mediaRecorder.onstop = async () => {
        stream.getTracks().forEach(track => track.stop());
        const audioBlob = new Blob(audioChunksRef.current, { type: mediaRecorder.mimeType });
        await transcribeAudio(audioBlob);
      };
      
      mediaRecorder.start();
      setIsRecording(true);
      toast.success('Enregistrement démarré');
    } catch (error) {
      toast.error('Impossible de démarrer l\'enregistrement');
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      toast.info('Transcription en cours...');
    }
  };

  const transcribeAudio = async (audioBlob) => {
    setIsTranscribing(true);
    try {
      const formDataUpload = new FormData();
      formDataUpload.append('file', audioBlob, 'recording.webm');
      
      const response = await api.post('/features/transcribe-audio', formDataUpload, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      if (response.data?.transcript) {
        setFormData(prev => ({
          ...prev,
          brief: prev.brief ? `${prev.brief}\n\n${response.data.transcript}` : response.data.transcript
        }));
        toast.success('Transcription réussie !');
      }
    } catch (error) {
      toast.error('Erreur lors de la transcription');
    } finally {
      setIsTranscribing(false);
    }
  };

  // Filter features
  const filteredFeatures = features.filter(f => 
    f.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    f.description?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const getStatusBadge = (status) => {
    const config = STATUS_CONFIG[status] || STATUS_CONFIG.idea;
    const Icon = config.icon;
    return (
      <Badge className={cn("text-xs border flex items-center gap-1", config.color)}>
        <Icon className="w-3 h-3" />
        {config.label}
      </Badge>
    );
  };

  const getSpaceBadge = (space) => {
    const config = SPACE_CONFIG[space] || SPACE_CONFIG.global;
    const Icon = config.icon;
    return (
      <span className={cn("inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full", config.color)}>
        <Icon className="w-3 h-3" />
        {config.label}
      </span>
    );
  };

  return (
    <MainLayout>
      <div className="max-w-full mx-auto">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
          <div>
            <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-2" data-testid="feature-tracking-title">
              <BarChart3 className="w-7 h-7 text-indigo-500" />
              Suivi des fonctionnalités
            </h1>
            <p className="text-slate-500">
              {filteredFeatures.length} fonctionnalité(s) • Gérez le développement de l&apos;application
            </p>
          </div>
          <div className="flex gap-2">
            <Button 
              variant="outline" 
              onClick={handleSeedFeatures}
              disabled={seeding}
              data-testid="seed-features-btn"
            >
              {seeding ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-2" />}
              Initialiser les fonctionnalités
            </Button>
            <Button onClick={openCreateDialog} className="bg-indigo-600 hover:bg-indigo-700" data-testid="add-feature-btn">
              <Plus className="w-4 h-4 mr-2" />
              Ajouter une fonctionnalité
            </Button>
          </div>
        </div>

        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-3 md:grid-cols-6 gap-3 mb-6">
            {Object.entries(STATUS_CONFIG).map(([key, config]) => {
              const StatusIcon = config.icon;
              const count = stats.totals?.[key] || 0;
              return (
                <Card 
                  key={key} 
                  className={cn(
                    "cursor-pointer hover:shadow-md transition-shadow",
                    statusFilter === key && "ring-2 ring-indigo-500"
                  )}
                  onClick={() => setStatusFilter(statusFilter === key ? 'all' : key)}
                >
                  <CardContent className="p-3">
                    <div className="flex items-center gap-2 mb-1">
                      <StatusIcon className="w-4 h-4" />
                      <span className="text-xs font-medium truncate">{config.label}</span>
                    </div>
                    <p className="text-2xl font-bold">{count}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}

        {/* Filters */}
        <Card className="mb-6">
          <CardContent className="p-4">
            <div className="flex flex-col sm:flex-row gap-4">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  placeholder="Rechercher une fonctionnalité..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10"
                />
              </div>
              <Select value={spaceFilter} onValueChange={setSpaceFilter}>
                <SelectTrigger className="w-[150px]">
                  <Filter className="w-4 h-4 mr-2" />
                  <SelectValue placeholder="Espace" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tous</SelectItem>
                  {Object.entries(SPACE_CONFIG).map(([key, config]) => (
                    <SelectItem key={key} value={key}>{config.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={categoryFilter} onValueChange={setCategoryFilter}>
                <SelectTrigger className="w-[150px]">
                  <SelectValue placeholder="Catégorie" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Toutes</SelectItem>
                  {CATEGORIES.map(cat => (
                    <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        {/* Features Table */}
        {loading ? (
          <div className="flex justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
          </div>
        ) : filteredFeatures.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center">
              <Lightbulb className="w-12 h-12 mx-auto mb-3 text-slate-300" />
              <p className="text-slate-500 mb-4">Aucune fonctionnalité trouvée</p>
              <div className="flex gap-3 justify-center">
                <Button variant="outline" onClick={handleSeedFeatures} disabled={seeding}>
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Charger les fonctionnalités existantes
                </Button>
                <Button onClick={openCreateDialog}>
                  <Plus className="w-4 h-4 mr-2" />
                  Créer une fonctionnalité
                </Button>
              </div>
            </CardContent>
          </Card>
        ) : (
          <Card className="overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full" data-testid="features-table">
                <thead className="bg-slate-50 border-b">
                  <tr>
                    <th className="text-left p-4 font-medium text-slate-600">Fonctionnalité</th>
                    <th className="text-left p-4 font-medium text-slate-600 w-24">Espace</th>
                    <th className="text-left p-4 font-medium text-slate-600 w-32">Catégorie</th>
                    <th className="text-center p-4 font-medium text-slate-600 w-36">Statut</th>
                    <th className="text-center p-4 font-medium text-slate-600 w-24">Priorité</th>
                    <th className="text-left p-4 font-medium text-slate-600 w-24">Version</th>
                    <th className="text-right p-4 font-medium text-slate-600 w-20">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredFeatures.map((feature) => {
                    const priorityConfig = PRIORITY_CONFIG[feature.priority] || PRIORITY_CONFIG.medium;
                    const categoryLabel = CATEGORIES.find(c => c.value === feature.category)?.label || feature.category;
                    
                    return (
                      <tr key={feature.id} className="border-b hover:bg-slate-50 transition-colors">
                        <td className="p-4">
                          <div>
                            <p className="font-medium text-slate-800">{feature.name}</p>
                            {feature.description && (
                              <p className="text-xs text-slate-500 mt-0.5 line-clamp-1">{feature.description}</p>
                            )}
                          </div>
                        </td>
                        <td className="p-4">
                          {getSpaceBadge(feature.space)}
                        </td>
                        <td className="p-4">
                          <span className="text-sm text-slate-600">{categoryLabel}</span>
                        </td>
                        <td className="p-4 text-center">
                          <Select
                            value={feature.status}
                            onValueChange={(value) => handleStatusChange(feature.id, value)}
                          >
                            <SelectTrigger className="w-[140px] h-8 text-xs">
                              <SelectValue>
                                {getStatusBadge(feature.status)}
                              </SelectValue>
                            </SelectTrigger>
                            <SelectContent>
                              {Object.entries(STATUS_CONFIG).map(([key, config]) => (
                                <SelectItem key={key} value={key}>
                                  <span className="flex items-center gap-2">
                                    <config.icon className="w-3 h-3" />
                                    {config.label}
                                  </span>
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </td>
                        <td className="p-4 text-center">
                          <Badge className={cn("text-xs", priorityConfig.color)}>
                            {priorityConfig.label}
                          </Badge>
                        </td>
                        <td className="p-4">
                          <span className="text-sm text-slate-500">{feature.target_version || '-'}</span>
                        </td>
                        <td className="p-4 text-right">
                          <div className="flex items-center justify-end gap-1">
                            <Button 
                              variant="ghost" 
                              size="icon" 
                              className="h-8 w-8"
                              onClick={() => openCommentsDialog(feature)}
                              title="Commentaires"
                              data-testid={`comments-btn-${feature.id}`}
                            >
                              <MessageSquare className="w-4 h-4 text-slate-500" />
                            </Button>
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button variant="ghost" size="icon" className="h-8 w-8">
                                  <MoreVertical className="w-4 h-4" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end">
                                <DropdownMenuItem onClick={() => openCommentsDialog(feature)}>
                                  <MessageSquare className="w-4 h-4 mr-2" />
                                  Commentaires
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={() => openEditDialog(feature)}>
                                  <Pencil className="w-4 h-4 mr-2" />
                                  Modifier
                                </DropdownMenuItem>
                                <DropdownMenuItem 
                                  className="text-red-600" 
                                  onClick={() => handleDelete(feature.id)}
                                  disabled={deletingId === feature.id}
                                >
                                  {deletingId === feature.id ? (
                                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                  ) : (
                                    <Trash2 className="w-4 h-4 mr-2" />
                                  )}
                                  Supprimer
                                </DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </Card>
        )}
      </div>

      {/* Feature Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="sm:max-w-xl">
          <DialogHeader>
            <DialogTitle>
              {editingFeature ? 'Modifier la fonctionnalité' : 'Nouvelle fonctionnalité'}
            </DialogTitle>
            <DialogDescription>
              {editingFeature 
                ? 'Modifiez les détails de cette fonctionnalité'
                : 'Ajoutez une nouvelle fonctionnalité à suivre'
              }
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {/* Name */}
            <div className="space-y-2">
              <Label>Nom de la fonctionnalité *</Label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                placeholder="Ex: Système de notifications push"
              />
            </div>

            {/* Description */}
            <div className="space-y-2">
              <Label>Description</Label>
              <Textarea
                value={formData.description}
                onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                placeholder="Décrivez brièvement la fonctionnalité..."
                rows={2}
              />
            </div>

            {/* Grid */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Espace</Label>
                <Select 
                  value={formData.space} 
                  onValueChange={(value) => setFormData(prev => ({ ...prev, space: value }))}
                >
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {Object.entries(SPACE_CONFIG).map(([key, config]) => (
                      <SelectItem key={key} value={key}>{config.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Catégorie</Label>
                <Select 
                  value={formData.category} 
                  onValueChange={(value) => setFormData(prev => ({ ...prev, category: value }))}
                >
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {CATEGORIES.map(cat => (
                      <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Statut</Label>
                <Select 
                  value={formData.status} 
                  onValueChange={(value) => setFormData(prev => ({ ...prev, status: value }))}
                >
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {Object.entries(STATUS_CONFIG).map(([key, config]) => (
                      <SelectItem key={key} value={key}>{config.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Priorité</Label>
                <Select 
                  value={formData.priority} 
                  onValueChange={(value) => setFormData(prev => ({ ...prev, priority: value }))}
                >
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {Object.entries(PRIORITY_CONFIG).map(([key, config]) => (
                      <SelectItem key={key} value={key}>{config.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Target Version */}
            <div className="space-y-2">
              <Label>Version cible</Label>
              <Input
                value={formData.target_version}
                onChange={(e) => setFormData(prev => ({ ...prev, target_version: e.target.value }))}
                placeholder="Ex: v2.0.0"
              />
            </div>

            {/* Brief with voice */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label>Brief / Notes</Label>
                <Button
                  type="button"
                  variant={isRecording ? "destructive" : "outline"}
                  size="sm"
                  onClick={isRecording ? stopRecording : startRecording}
                  disabled={isTranscribing}
                >
                  {isTranscribing ? (
                    <><Loader2 className="w-4 h-4 mr-1 animate-spin" /> Transcription...</>
                  ) : isRecording ? (
                    <><MicOff className="w-4 h-4 mr-1" /> Arrêter</>
                  ) : (
                    <><Mic className="w-4 h-4 mr-1" /> Dicter</>
                  )}
                </Button>
              </div>
              <Textarea
                value={formData.brief}
                onChange={(e) => setFormData(prev => ({ ...prev, brief: e.target.value }))}
                placeholder="Notes de développement, contraintes, attentes..."
                rows={4}
              />
              {isRecording && (
                <div className="flex items-center gap-2 text-red-500 text-sm">
                  <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-md bg-red-500/10">
                    <span className="w-1.5 h-1.5 bg-red-500 rounded-full animate-[pulse_1.5s_ease-in-out_infinite]" />
                    <span className="text-xs font-medium text-red-600 uppercase tracking-wide">Rec</span>
                  </span>
                </div>
              )}
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDialog(false)}>
              Annuler
            </Button>
            <Button onClick={handleSave} disabled={savingFeature}>
              {savingFeature && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              {editingFeature ? 'Mettre à jour' : 'Créer'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Comments Dialog */}
      <Dialog open={showCommentsDialog} onOpenChange={setShowCommentsDialog}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <MessageSquare className="w-5 h-5 text-indigo-600" />
              Commentaires
            </DialogTitle>
            {selectedFeatureForComments && (
              <DialogDescription>
                {selectedFeatureForComments.name}
              </DialogDescription>
            )}
          </DialogHeader>
          
          <div className="py-4">
            {/* Comments List */}
            <ScrollArea className="h-[300px] pr-4">
              {loadingComments ? (
                <div className="flex items-center justify-center h-full">
                  <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
                </div>
              ) : comments.length === 0 ? (
                <div className="text-center py-8 text-slate-500">
                  <MessageSquare className="w-10 h-10 mx-auto mb-2 opacity-30" />
                  <p>Aucun commentaire</p>
                  <p className="text-sm">Soyez le premier à commenter</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {comments.map((comment) => (
                    <div key={comment.id} className="bg-slate-50 rounded-lg p-3">
                      <div className="flex items-start justify-between">
                        <div className="flex items-center gap-2">
                          <div className="w-8 h-8 rounded-full bg-indigo-100 flex items-center justify-center">
                            <User className="w-4 h-4 text-indigo-600" />
                          </div>
                          <div>
                            <p className="font-medium text-sm text-slate-800">
                              {comment.author_name}
                            </p>
                            <p className="text-xs text-slate-400">
                              {formatCommentDate(comment.created_at)}
                            </p>
                          </div>
                        </div>
                        {(comment.author_id === user?.id || user?.role === 'agency') && (
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-6 w-6 text-slate-400 hover:text-red-500"
                            onClick={() => handleDeleteComment(comment.id)}
                          >
                            <Trash2 className="w-3 h-3" />
                          </Button>
                        )}
                      </div>
                      <p className="mt-2 text-sm text-slate-600 whitespace-pre-wrap">
                        {comment.content}
                      </p>
                    </div>
                  ))}
                </div>
              )}
            </ScrollArea>
            
            {/* Add Comment */}
            <div className="mt-4 pt-4 border-t">
              <div className="flex gap-2">
                <Textarea
                  value={newComment}
                  onChange={(e) => setNewComment(e.target.value)}
                  placeholder="Ajouter un commentaire..."
                  rows={2}
                  className="flex-1 resize-none"
                />
                <Button
                  onClick={handleAddComment}
                  disabled={!newComment.trim() || sendingComment}
                  className="self-end"
                >
                  {sendingComment ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Send className="w-4 h-4" />
                  )}
                </Button>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </MainLayout>
  );
};

export default FeatureTrackingPage;
